'use client'

import React from 'react'
import type { HubspotFormBlock as HubspotFormBlockProps, Hubspotform } from '@/payload-types'
import { useHubSpotForm } from './hooks/useHubSpotForm'
import styles from './styles.module.css'
import { getBlockPadding } from '@/utilities/getBlockPadding'

// Type guard to check if hubspotform is populated object
function isHubspotformObject(hubspotform: HubspotFormBlockProps['hubspotform']): hubspotform is Hubspotform {
  return typeof hubspotform === 'object' && hubspotform !== null && 'portalId' in hubspotform
}

// Type-safe utility to extract form data
function getFormData(hubspotform: HubspotFormBlockProps['hubspotform']) {
  if (isHubspotformObject(hubspotform)) {
    return {
      portalId: hubspotform.portalId,
      formId: hubspotform.formId,
      formName: hubspotform.title,
      region: hubspotform.region,
    }
  }
  return null
}

const HubSpotFormRenderer: React.FC<{ hubspotform: HubspotFormBlockProps['hubspotform'] }> = ({ hubspotform }) => {
  const formData = getFormData(hubspotform)

  // Always call the hook, regardless of whether we have form data
  const { containerRef, isLoading, error } = useHubSpotForm({
    portalId: formData?.portalId || '',
    formId: formData?.formId || '',
    formName: formData?.formName || '',
    region: formData?.region,
  })

  if (!formData) {
    return (
      <div className="p-4 text-center text-gray-500">
        Form configuration unavailable. Please try again later.
      </div>
    )
  }

  if (formData.portalId && formData.formId) {
    if (error) {
      return (
        <div className={styles.errorContainer}>
          {error}
        </div>
      )
    }

    return (
      <>
        {isLoading && (
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}></div>
            Loading form...
          </div>
        )}
        <div ref={containerRef} />
      </>
    )
  }

  return (
    <div className="p-4 text-center text-gray-500">
      Form temporarily unavailable. Please try again later.
    </div>
  )
}

export const HubspotFormBlock: React.FC<HubspotFormBlockProps & { disableContainer?: boolean }> = ({
  hubspotform,
  paddingTop,
  paddingBottom,
  disableContainer,
}) => {
  if (disableContainer) {
    return (
      <div className="w-full bg-civo-cloud rounded-[8px] p-3 sm:p-4 lg:p-6">
        <div className={styles.form}>
          <HubSpotFormRenderer hubspotform={hubspotform} />
        </div>
      </div>
    )
  }

  // Get padding classes based on layout options
  const paddingClasses = getBlockPadding({
    paddingTop: paddingTop ?? true,
    paddingBottom: paddingBottom ?? true,
  })

  return (
    <div className={`flex justify-center ${paddingClasses}`}>
      <div className="w-full bg-civo-cloud rounded-[8px] p-3 sm:p-4 lg:p-6">
        <div className={styles.form}>
          <HubSpotFormRenderer hubspotform={hubspotform} />
        </div>
      </div>
    </div>
  )
}